// Developer: Ankit Gupta
import { BrowserRouter } from 'react-router-dom';
import { ConfigProvider, theme, App as AntdApp } from 'antd';
import arEG from 'antd/locale/ar_EG';
import enUS from 'antd/locale/en_US';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import {
  useApplyTheme,
  useResolvedTheme,
  type ResolvedTheme,
} from '@/theme/useTheme';
import { AuthProvider } from '@/auth/AuthProvider';
import { DevAuthProvider } from '@/auth/DevAuthProvider';
import { AppRoutes } from '@/router';
import { directionOf, useLanguage } from '@/i18n';

const useDevAuth = import.meta.env.VITE_DEV_AUTH === 'true';

// ─── React Query client ───────────────────────────────────────────────────────

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 1000 * 60, // 1 minute default
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

// ─── Ant Design theme ─────────────────────────────────────────────────────────
// Ant Design computes its derived colours in JavaScript and cannot read a CSS
// variable, so the palette is repeated here per mode. These values are the same
// ones styles/theme.css defines; if one moves, move both.

const SHARED_TOKENS = {
  // IBM Plex Sans for reading; headings take Outfit from theme.css. Neither
  // has Arabic glyphs, so Arabic text falls through to IBM Plex Sans Arabic
  // glyph by glyph; English text never reaches it and is unchanged.
  fontFamily:
    "'IBM Plex Sans', 'IBM Plex Sans Arabic', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  fontSize: 14,
  // Level 3 is the product's page title, so it matches .dp-page-title.
  fontSizeHeading3: 30,
  fontSizeHeading4: 19,
  borderRadius: 10,
  borderRadiusLG: 14,
  borderRadiusSM: 6,
  wireframe: false,
};

// AAFAQ red on two canvases (docs/Theme/): "ink" slate greys in the dark (Option C, 23 Oct 2026),
// "graphite" stone greys in the light. These repeat styles/theme.css, because antd derives its
// hover and active shades from real colours and cannot read a CSS variable.
const DARK_TOKENS = {
  colorPrimary: '#dc2626',
  colorInfo: '#38bdf8',
  colorLink: '#f87171',
  controlOutline: 'rgba(220, 38, 38, 0.24)',
  colorSuccess: '#4ade80',
  colorWarning: '#f59e0b',
  colorError: '#f87171',
  colorBgBase: '#15191d',
  colorBgContainer: '#1f2327',
  colorBgElevated: '#2a2f33',
  colorBgLayout: '#15191d',
  colorBorder: '#2c3135',
  colorBorderSecondary: '#262a2e',
  colorText: '#dadfe3',
  colorTextSecondary: '#c2c7cb',
  colorTextTertiary: '#9ba1a6',
  // the selected option of a select, an autocomplete, a cascader: the same red-tinted row as a menu
  controlItemBgActive: '#2a1a1c',
};

const LIGHT_TOKENS = {
  colorPrimary: '#b91c1c',
  colorInfo: '#0284c7',
  colorLink: '#b91c1c',
  controlOutline: 'rgba(185, 28, 28, 0.18)',
  colorSuccess: '#15803d',
  colorWarning: '#b45309',
  colorError: '#dc2626',
  colorBgBase: '#ffffff',
  colorBgContainer: '#ffffff',
  colorBgElevated: '#ffffff',
  colorBgLayout: '#f5f5f4',
  colorBorder: '#e7e5e4',
  colorBorderSecondary: '#f0eeed',
  colorText: '#1c1917',
  colorTextSecondary: '#57534e',
  colorTextTertiary: '#78716c',
  controlItemBgActive: '#fef2f2',
};

function antdThemeFor(resolved: ResolvedTheme) {
  const dark = resolved === 'dark';
  const palette = dark ? DARK_TOKENS : LIGHT_TOKENS;
  return {
    algorithm: dark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: { ...SHARED_TOKENS, ...palette },
    components: {
      Layout: {
        siderBg: dark ? '#1b1f23' : '#1c1917',
        headerBg: dark ? '#1f2327' : '#ffffff',
        bodyBg: palette.colorBgLayout,
        headerPadding: '0 28px',
      },
      Card: {
        borderRadiusLG: 14,
        paddingLG: 24,
        headerFontSize: 16,
        colorBorderSecondary: palette.colorBorderSecondary,
      },
      Table: {
        headerBg: 'transparent',
        headerColor: palette.colorTextTertiary,
        borderColor: palette.colorBorderSecondary,
        rowHoverBg: dark ? '#262a2e' : '#fafaf9',
        headerSplitColor: 'transparent',
        cellPaddingBlock: 12,
        headerBorderRadius: 8,
      },
      Button: {
        fontWeight: 500,
        primaryShadow: dark
          ? '0 6px 18px rgba(220, 38, 38, 0.24)'
          : '0 6px 18px rgba(185, 28, 28, 0.2)',
        defaultShadow: 'none',
        algorithm: true,
      },
      Menu: {
        itemBorderRadius: 10,
        itemHeight: 40,
        iconSize: 18,
        subMenuItemBg: 'transparent',
        // a selected menu item is the same red-tinted row as every other selection (theme.css --dp-sel-*)
        itemSelectedBg: dark ? '#2a1a1c' : '#fef2f2',
        itemSelectedColor: dark ? '#dadfe3' : '#1c1917',
      },
      Tag: { borderRadiusSM: 6 },
      Modal: { borderRadiusLG: 16 },
      Segmented: { borderRadius: 8 },
      Statistic: { titleFontSize: 14 },
      Typography: {
        fontFamilyCode: "'IBM Plex Mono', monospace",
      },
    },
  };
}

// ─── App ──────────────────────────────────────────────────────────────────────

export function App() {
  const resolved = useResolvedTheme();
  useApplyTheme(resolved);
  // antd's own words (pagination, empty states, the date picker's buttons) and
  // its direction follow the interface language. dayjs is deliberately left
  // in English: a picked date is formatted with it and sent to the server and
  // to BI Publisher, and an Arabic month name there would be a wrong value.
  const language = useLanguage();

  return (
    <ConfigProvider
      theme={antdThemeFor(resolved)}
      direction={directionOf(language)}
      locale={language === 'ar' ? arEG : enUS}
    >
      <AntdApp>
        <QueryClientProvider client={queryClient}>
          {useDevAuth ? (
            <DevAuthProvider>
              <BrowserRouter>
                <AppRoutes />
              </BrowserRouter>
            </DevAuthProvider>
          ) : (
            <AuthProvider>
              <BrowserRouter>
                <AppRoutes />
              </BrowserRouter>
            </AuthProvider>
          )}
        </QueryClientProvider>
      </AntdApp>
    </ConfigProvider>
  );
}

export default App;
